<template>
    <footer class="bg-admin-primary text-white pt-8 pb-4 px-4">
        <div class="container mx-auto grid grid-cols-1 gap-8" :class="colunasGrade">
            <div>
                <img src="../../Assets/Logo PMP BRANCO H.png" alt="Prefeitura Municipal de Parnaíba"
                    class="h-16 md:h-20 mb-5" />
                <h4 v-if="aparece('endereco') || aparece('contatos')" class="font-bold text-lg mb-3">ENDEREÇO E CONTATOS</h4>
                <div class="text-sm space-y-2">
                    <p class="font-semibold">
                        Prefeitura Municipal de Parnaíba
                    </p>
                    <template v-if="aparece('endereco')">
                    <address v-if="rodape.logradouro" class="not-italic">
                        {{ rodape.logradouro
                        }}{{ rodape.numero ? `, ${rodape.numero}` : "" }}<br />
                        {{ rodape.bairro }}<br />
                        {{ rodape.cidade }} – {{ rodape.estado }} | CEP:
                        {{ rodape.cep }}
                    </address>
                    <p v-else class="not-italic text-gray-300 italic">
                        Endereço não configurado
                    </p>
                    </template>
                    <template v-if="aparece('contatos')">
                    <div v-if="rodape.numeros?.length" class="mt-2">
                        <div v-for="(num, i) in rodape.numeros" :key="i" class="mb-1">
                            <span class="font-medium">{{ num.descricao || "Telefone" }}:</span><br />
                            <a v-if="num.numero" :href="`tel:${num.numero.replace(/\D/g, '')}`"
                                class="hover:text-blue-200 underline">
                                {{ num.numero }}
                            </a>
                        </div>
                    </div>
                    <p v-else class="mt-2 text-gray-300 italic">
                        Nenhum contato configurado
                    </p>
                    </template>
                    <div v-if="aparece('funcionamento') && rodape.horario_funcionamento" class="flex items-start gap-3 pt-3">
                        <Clock class="w-6 h-6 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <p>
                            <span class="block font-bold uppercase tracking-wide">Funcionamento</span>
                            <span class="text-white/90">{{ rodape.horario_funcionamento }}</span>
                        </p>
                    </div>
                </div>
            </div>
            <div v-if="aparece('links_importantes')">
                <h4 class="font-bold text-lg mb-3">LINKS IMPORTANTES</h4>
                <ul v-if="rodape.links_importantes?.length" class="space-y-2 text-sm">
                    <li v-for="(link, i) in rodape.links_importantes" :key="i">
                        <a :href="link.link" target="_blank" rel="noopener noreferrer"
                            class="hover:text-blue-200 transition">
                            {{ link.descricao }}
                        </a>
                    </li>
                </ul>
                <p v-else class="text-sm text-gray-300 italic">
                    Nenhum link configurado
                </p>
            </div>
            <div v-if="aparece('links_uteis') || (aparece('redes_sociais') && rodape.redes_sociais?.length)">
                <template v-if="aparece('links_uteis')">
                <h4 class="font-bold text-lg mb-3">LINKS ÚTEIS</h4>
                <ul v-if="rodape.links_uteis?.length" class="space-y-2 text-sm">
                    <li v-for="(link, i) in rodape.links_uteis" :key="i">
                        <a :href="link.link" target="_blank" rel="noopener noreferrer"
                            class="hover:text-blue-200 transition">
                            {{ link.descricao }}
                        </a>
                    </li>
                </ul>
                <p v-else class="text-sm text-gray-300 italic mb-4">
                    Nenhum link útil configurado
                </p>
                </template>
                <div :class="{ 'pt-4': aparece('links_uteis') }" v-if="aparece('redes_sociais') && rodape.redes_sociais?.length">
                    <h4 class="font-bold text-lg mb-3">REDES SOCIAIS</h4>
                    <div class="flex space-x-4 mb-5">
                        <a v-for="(rs, i) in rodape.redes_sociais" :key="i" :href="rs.link" target="_blank"
                            rel="noopener noreferrer" :aria-label="rs.descricao" class="hover:opacity-80 transition">
                            <component :is="getSocialIcon(rs.icone)" v-if="getSocialIcon(rs.icone)" class="w-8 h-8" />
                            <Globe v-else class="w-8 h-8" />
                        </a>
                    </div>
                </div>
            </div>
            <div v-if="aparece('mapa')" class="flex flex-col">
                <h4 class="font-bold text-lg mb-3">Localização</h4>
                <MapSectionSimple class="rounded-lg overflow-hidden h-48 shadow" style="height: 400px" />
            </div>
        </div>
        <div class="border-t border-blue-800 mt-8 pt-4 text-center text-sm">
            <p>
                © {{ new Date().getFullYear() }} Prefeitura Municipal de
                Parnaíba. Todos os direitos reservados.
            </p>
            <p class="mt-1">
                Produzido por: Núcleo de Tecnologia da Informação
            </p>
        </div>
    </footer>
</template>
<script setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import MapSectionSimple from "@/Components/Welcom/MapSectionSimple.vue";
import {
    Facebook,
    Instagram,
    Youtube,
    Twitter,
    Linkedin,
    Globe,
    Clock,
} from "lucide-vue-next";
const rodape = computed(() => usePage().props.rodape);

// Seção desligada no painel (Configuração do Rodapé) não aparece; sem configuração, todas aparecem
const aparece = (secao) => rodape.value?.secoes_visiveis?.[secao] !== false;

// Classes fixas para o Tailwind encontrar no build
const COLUNAS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" };
const colunasGrade = computed(() => {
    const visiveis = 1
        + Number(aparece("links_importantes"))
        + Number(aparece("links_uteis") || (aparece("redes_sociais") && !!rodape.value?.redes_sociais?.length))
        + Number(aparece("mapa"));
    return COLUNAS[visiveis];
});
const getSocialIcon = (name) => {
    const normalized = (name || "").toLowerCase().trim();
    if (normalized.includes("facebook")) return Facebook;
    if (normalized.includes("instagram")) return Instagram;
    if (normalized.includes("youtube") || normalized.includes("vídeo"))
        return Youtube;
    if (normalized.includes("twitter") || normalized.includes("x.com"))
        return Twitter;
    if (normalized.includes("linkedin")) return Linkedin;
    return null;
};
</script>
